//* 주문 수령인(받으시는 분) 수정 모달 — 상주를 잘못 고르고 주문했거나 이름/번호가 틀린 경우 교정
//* 해당 상가(fevent)의 상주 명단에서 선택하거나, 전화로 들은 이름·번호를 직접 입력한다.
//* patch-detail dot-notation 부분 업데이트 — 중첩 객체({ deliveryDetail: {...} })로 보내면
//* mongoose가 deliveryDetail 서브문서를 통째로 교체해 배송상태·주소가 날아가므로 금지.

import { FC, useCallback, useEffect, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { feventMemberListForSellerUrl } from '@shared/api/fevent/fevent.hook';
import { Member } from '@shared/api/member/member.interface';
import { updateShopOrder } from '@shared/api/shopOrder/shopOrder.controller';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import TelField from '@shared/components/TelField';
import { TextField } from '@shared/components/TextField';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';
import {
  bareReceiverName,
  normalizeReceiverNameInput,
  receiverNameWithHonorific,
} from '@shared/utils/receiverName.util';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from 'react-query';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
}

interface ReceiverForm {
  receiverName: string;
  receiverPhoneNumber: string;
}

type ReceiverMember = Pick<
  Member,
  '_id' | 'fullName' | 'shortName' | 'relationType' | 'phoneNumber'
>;

interface FeventMemberListResponse {
  _id: string;
  memberOrderList?: ReceiverMember[];
}

//* 명단 조회 실패는 직접 입력으로 우회 가능하므로 안내만 — 원인 파악용으로 상태코드를 함께 노출
const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

//* 주문 생성 시 receiverName은 "OOO 상주님"으로 저장된다 — 선택 시 같은 형식으로 맞춘다.
//* 존칭 중복("OOO 상주님님") 방지 규칙은 receiverName.util 한 곳에 모아둔다.
const receiverNameOf = (member: ReceiverMember) =>
  receiverNameWithHonorific(member.fullName);

export const OrderReceiverEditModal: FC<Props> = function OrderReceiverEditModal({
  openId,
  order,
}: Props) {
  const { sellerRole } = useAuth();
  const [open, setOpen] = useRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const queryClient = useQueryClient();

  const currentName = order.deliveryDetail.receiverName;
  const currentPhoneNumber = order.deliveryDetail.receiverPhoneNumber;

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  const { register, control, handleSubmit, reset, setValue } = useForm<ReceiverForm>({
    defaultValues: {
      receiverName: currentName,
      receiverPhoneNumber: currentPhoneNumber,
    },
  });
  const telValue = useWatch({ control, name: 'receiverPhoneNumber', defaultValue: '' });

  //* 상주 명단 (모달 열릴 때 조회) — 없거나 실패해도 직접 입력은 가능해야 하므로 치명 오류 아님
  const [members, setMembers] = useState<ReceiverMember[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const feventId = order.bugo?.fevent?._id;
  //* useAuth의 sellerRole은 매 렌더 새 함수라 effect deps에 넣으면 무한 재조회가 된다 — 문자열로 고정
  const role = sellerRole();

  //* 열리는 순간에만 리시드 (편집 중 목록 refetch로 인한 입력값 클로버 방지 — order를 deps에 넣지 않음)
  useEffect(() => {
    if (!open) return;
    reset({
      receiverName: currentName,
      receiverPhoneNumber: currentPhoneNumber,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  useEffect(() => {
    if (!open) return;
    if (!feventId) {
      setMembers([]);
      setListError(
        '이 주문에 연결된 부고(상가) 정보가 없어 상주 명단을 불러올 수 없습니다.',
      );
      return;
    }
    setMembers(null);
    setListError(null);
    api_
      .get<FeventMemberListResponse>(feventMemberListForSellerUrl(feventId, role))
      .then(({ data }) => setMembers(data?.memberOrderList ?? []))
      .catch((err) => {
        console.error(err);
        setMembers([]);
        const status = statusOf(err);
        setListError(
          `상주 명단을 불러오지 못했습니다${
            status ? ` (${status})` : ''
          }. 이름을 직접 입력해주세요.`,
        );
      });
  }, [open, feventId, role]);

  const onSubmit: SubmitHandler<ReceiverForm> = useCallback(
    async (data) => {
      //* 직접 입력에 존칭이 겹쳐 들어오면 하나로 접는다 (알림 문자에서 "상주님님"이 되는 걸 방지)
      const receiverName = normalizeReceiverNameInput(data.receiverName);
      const receiverPhoneNumber = String(data.receiverPhoneNumber).replace(/[^0-9]/g, '');
      if (!receiverName) {
        notificateError(notifiacationInstance, '받으시는 분 이름을 입력해주세요.');
        return;
      }
      if (receiverPhoneNumber && !/^0[0-9]{8,10}$/.test(receiverPhoneNumber)) {
        notificateError(notifiacationInstance, '올바른 연락처를 입력해주세요.');
        return;
      }

      const orderPatch = {
        _id: order._id,
        'deliveryDetail.receiverName': receiverName,
        'deliveryDetail.receiverPhoneNumber': receiverPhoneNumber,
      };

      setSubmitting(true);
      try {
        await updateShopOrder(orderPatch, role);
        //* 이 카드는 배송관리·미결제·입금대기 목록에서 함께 쓰이므로 shop-order 조회를 통째로 무효화
        //* (문자열 queryKey는 query.queryKey[0]로 못 잡는다 — invalidateQueriesByUrlPart 주석 참고)
        await invalidateQueriesByUrlPart(queryClient, 'shop-order');
        notificateSuccess(notifiacationInstance, '받으시는 분 수정 완료');
        onCloseModal();
      } catch (err) {
        console.error(err);
        notificateError(notifiacationInstance, '받으시는 분 수정에 실패했습니다.');
      } finally {
        setSubmitting(false);
      }
    },
    [notifiacationInstance, onCloseModal, order._id, queryClient, role],
  );

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">받으시는 분 수정</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            상주를 잘못 지정했거나 이름·연락처가 틀린 경우 교정합니다. 배송상태·결제
            정보는 변경되지 않으며 고객에게 알림이 발송되지 않습니다.
          </p>
        </div>
        <div>
          <p className="pb-1 text-sm font-medium text-gray-600">
            상가 상주 명단
            {order.bugo?.fevent?.deceasedInfo?.name
              ? ` (고 ${order.bugo.fevent.deceasedInfo.name})`
              : ''}
          </p>
          {members === null ? (
            <p className="text-sm text-gray-500">불러오는 중…</p>
          ) : members.length > 0 ? (
            <div className="grid grid-cols-2 gap-1">
              {members.map((member) => {
                const selected = bareReceiverName(currentName) === member.fullName;
                return (
                  <button
                    type="button"
                    key={member._id}
                    className={`min-w-0 rounded-md border p-2 text-left hover:bg-gray-50 ${
                      selected ? 'border-cyan-500 bg-cyan-50/40' : 'bg-white'
                    }`}
                    onClick={() => {
                      setValue('receiverName', receiverNameOf(member), {
                        shouldDirty: true,
                      });
                      setValue('receiverPhoneNumber', member.phoneNumber ?? '', {
                        shouldDirty: true,
                      });
                    }}
                  >
                    <p className="truncate text-sm font-medium text-gray-800">
                      {member.relationType}&nbsp;&nbsp;{member.fullName}
                      {selected && (
                        <span className="pl-1 text-xs text-cyan-700">(현재)</span>
                      )}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {phoneNumberStringFormator(member.phoneNumber)}
                    </p>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500">등록된 상주가 없습니다.</p>
          )}
          {listError && (
            <p className="break-keep pt-1 text-xs text-amber-600">{listError}</p>
          )}
          <p className="break-keep pt-1 text-xs text-gray-500">
            명단에 없으면 아래에 직접 입력하세요. (부고의 상주 정보 자체는 바뀌지 않고 이
            주문의 수령인 표기만 바뀝니다)
          </p>
        </div>
        <div className="space-y-3 font-bold">
          <div>
            <p className="pb-1 text-sm font-medium text-gray-600">받으시는 분</p>
            <TextField
              className="w-full"
              placeholder="OOO 상주님"
              {...register('receiverName', { required: true })}
            />
          </div>
          <div>
            <p className="pb-1 text-sm font-medium text-gray-600">연락처</p>
            <TelField
              value={String(telValue)}
              register={register}
              name="receiverPhoneNumber"
              className="w-full"
            />
          </div>
        </div>
        <Button
          className="button-rectangle h-10 theme-text-4 theme-bg-main"
          disabled={submitting}
          onClick={handleSubmit(onSubmit)}
        >
          {submitting ? '수정 중…' : '수정'}
        </Button>
      </div>
    </Modal>
  );
};
